// UI language for the admin console.
//
// Modeled on src/theme.js: a module-level reactive preference persisted per
// browser, resolved to a concrete locale, applied everywhere it matters. The
// preference is "auto" (follow the browser's language list — the default) or
// one of the 28 supported locale codes.
//
// setLanguage() applies, in order: vue-i18n locale (lazy-loading the locale's
// message chunk first), Vuetify's own locale (data-table paging, etc.),
// <html lang>, moment's locale, and the shared `localeRef` that locale-aware
// formatters/computeds depend on. It also mirrors the resolved locale into
// the `isi_lang` cookie on .isilive.ca so the sp2_auth login/recovery pages
// (a different origin, same site) greet the user in the same language.
//
// en.json is bundled eagerly; every other locale is a lazy webpack chunk.
// English is the fallback for any missing key, so a partially-translated
// build degrades readable, never blank.

import { ref } from "vue";
import { createI18n } from "vue-i18n";
import moment from "moment";
import en from "../locales/en.json";

export const LANGUAGE_STORAGE_KEY = "admin_console_language";
const LANGUAGE_COOKIE = "isi_lang";

export const SUPPORTED_LANGUAGES = [
  { code: "en", name: "English", nativeName: "English" },
  { code: "bg", name: "Bulgarian", nativeName: "Български" },
  { code: "cs", name: "Czech", nativeName: "Čeština" },
  { code: "da", name: "Danish", nativeName: "Dansk" },
  { code: "de", name: "German", nativeName: "Deutsch" },
  { code: "el", name: "Greek", nativeName: "Ελληνικά" },
  { code: "es", name: "Spanish", nativeName: "Español" },
  { code: "et", name: "Estonian", nativeName: "Eesti" },
  { code: "fi", name: "Finnish", nativeName: "Suomi" },
  { code: "fr", name: "French", nativeName: "Français" },
  { code: "fr-CA", name: "French (Canada)", nativeName: "Français (Canada)" },
  { code: "ga", name: "Irish", nativeName: "Gaeilge" },
  { code: "hr", name: "Croatian", nativeName: "Hrvatski" },
  { code: "hu", name: "Hungarian", nativeName: "Magyar" },
  { code: "it", name: "Italian", nativeName: "Italiano" },
  { code: "lt", name: "Lithuanian", nativeName: "Lietuvių" },
  { code: "lv", name: "Latvian", nativeName: "Latviešu" },
  { code: "mt", name: "Maltese", nativeName: "Malti" },
  { code: "nl", name: "Dutch", nativeName: "Nederlands" },
  { code: "no", name: "Norwegian", nativeName: "Norsk" },
  { code: "pl", name: "Polish", nativeName: "Polski" },
  { code: "pt", name: "Portuguese", nativeName: "Português" },
  { code: "ro", name: "Romanian", nativeName: "Română" },
  { code: "ru", name: "Russian", nativeName: "Русский" },
  { code: "sk", name: "Slovak", nativeName: "Slovenčina" },
  { code: "sl", name: "Slovenian", nativeName: "Slovenščina" },
  { code: "sv", name: "Swedish", nativeName: "Svenska" },
  { code: "uk", name: "Ukrainian", nativeName: "Українська" },
];

// Browser codes that map onto a supported locale under a different name.
const ALIASES = { nb: "no", nn: "no" };

const CANONICAL_BY_LOWER = {};
for (const lang of SUPPORTED_LANGUAGES) {
  CANONICAL_BY_LOWER[lang.code.toLowerCase()] = lang.code;
}

function canonicalToken(token) {
  if (!token) {
    return null;
  }
  const lower = String(token).toLowerCase();
  return (
    CANONICAL_BY_LOWER[lower] || CANONICAL_BY_LOWER[ALIASES[lower]] || null
  );
}

function readStoredMode() {
  try {
    const stored = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (stored === "auto" || canonicalToken(stored)) {
      return stored;
    }
  } catch {
    // localStorage unavailable (private mode) — fall through.
  }
  return "auto";
}

// Reactive so the topbar menu re-renders when the mode changes.
export const languageMode = ref(readStoredMode());

// The concrete locale currently applied. Locale-aware computeds and the
// format.js helpers read this ref so they re-evaluate on language change
// (moment.locale() alone does not invalidate Vue computeds).
export const localeRef = ref("en");

// Walk the browser preference list IN ORDER, resolving each candidate fully
// (exact match, alias, then base subtag) before trying the next — so
// ['fr-FR', 'de'] yields 'fr', never 'de'.
export function resolveLocale(mode) {
  if (mode !== "auto") {
    const explicit = canonicalToken(mode);
    if (explicit) {
      return explicit;
    }
  }
  const candidates =
    navigator.languages || (navigator.language ? [navigator.language] : []);
  for (const candidate of candidates) {
    const base = String(candidate || "").split("-")[0];
    const match = canonicalToken(candidate) || canonicalToken(base);
    if (match) {
      return match;
    }
  }
  return "en";
}

// The locale vue-i18n/Vuetify should be created with, so the first paint is
// already right for English-adjacent cases. Non-en locales still start on
// the bundled English and switch as soon as their chunk loads (installI18n).
export function initialLocale() {
  return resolveLocale(languageMode.value);
}

let i18nInstance = null;
let vuetifyInstance = null;

export function createAdminI18n() {
  i18nInstance = createI18n({
    legacy: false,
    globalInjection: true,
    locale: "en",
    fallbackLocale: "en",
    messages: { en },
    missingWarn: false,
    fallbackWarn: false,
  });
  return i18nInstance;
}

async function loadLocaleMessages(locale) {
  if (
    locale === "en" ||
    i18nInstance.global.availableLocales.includes(locale)
  ) {
    return;
  }
  const messages = await import(
    /* webpackChunkName: "locale-[request]" */ `../locales/${locale}.json`
  );
  i18nInstance.global.setLocaleMessage(locale, messages.default);
}

function momentLocaleFor(locale) {
  if (locale === "no") {
    return "nb";
  }
  return locale.toLowerCase();
}

function writeLanguageCookie(locale) {
  // One-way mirror for the sp2_auth pages. Scoped to .isilive.ca — on other
  // hosts (localhost dev, the .net admin names) the cookie can't reach the
  // auth origin, so the login redirect carries ?lang= instead (App.vue).
  if (!window.location.hostname.endsWith(".isilive.ca")) {
    return;
  }
  document.cookie = `${LANGUAGE_COOKIE}=${locale}; Domain=.isilive.ca; Path=/; Max-Age=31536000; SameSite=Lax; Secure`;
}

function applyLocale(locale) {
  localeRef.value = locale;
  i18nInstance.global.locale.value = locale;
  if (vuetifyInstance) {
    vuetifyInstance.locale.current.value = vuetifyLocaleName(locale);
  }
  document.documentElement.lang = locale;
  moment.locale(momentLocaleFor(locale));
}

// Vuetify has packs for every supported locale except ga/mt (English) and
// fr-CA (shares fr); its export names strip the dash (frCA would be fr-CA's
// name if it existed). See src/i18n/vuetifyLocales.js for the message set.
export function vuetifyLocaleName(locale) {
  if (locale === "fr-CA") {
    return "fr";
  }
  if (locale === "ga" || locale === "mt") {
    return "en";
  }
  return locale;
}

// Guard against out-of-order async completion: if the user picks French and
// then German, a slow French chunk must not win after German applied.
let switchToken = 0;

export async function setLanguage(mode) {
  const token = ++switchToken;
  const locale = resolveLocale(mode);

  try {
    await loadLocaleMessages(locale);
  } catch (err) {
    // Chunk failed to load (offline, deploy in flight): keep the current
    // language and don't persist the failed choice.
    console.error("Failed to load locale", locale, err);
    return;
  }
  if (token !== switchToken) {
    return;
  }

  languageMode.value = mode;
  try {
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, mode);
  } catch {
    // Private mode — the choice just won't persist.
  }
  writeLanguageCookie(locale);
  applyLocale(locale);
}

// Called once from main.js with the created Vuetify instance. Applies the
// stored/detected language (loading its chunk if needed) and keeps "auto"
// mode following live browser-language changes.
export function installI18n(vuetify) {
  vuetifyInstance = vuetify;
  applyLocale("en");
  setLanguage(languageMode.value);

  window.addEventListener("languagechange", function () {
    if (languageMode.value === "auto") {
      setLanguage("auto");
    }
  });
}

// Append the resolved locale to an auth-UI URL as ?lang=. The isi_lang
// cookie doesn't reach auth.isilive.ca from the .net admin hosts (a .net
// page can't set a .ca cookie), so login redirects carry the language in
// the URL; the auth UI persists it into its own cookie on first load.
export function appendLangParam(url) {
  if (!url) {
    return url;
  }
  try {
    const parsed = new URL(url);
    parsed.searchParams.set("lang", localeRef.value);
    return parsed.toString();
  } catch {
    return url;
  }
}

// Route names double as identifiers ($router.push({name})), so they stay
// English; page titles translate through this map instead of meta edits.
const ROUTE_TITLE_KEYS = {
  Dashboard: "routes.dashboard",
  "Client Stream": "routes.clientStream",
  "Client Streams": "routes.clientStreams",
  "User Profile": "routes.userProfile",
  Users: "routes.users",
  "User Detail": "routes.userDetail",
  "Audit Log": "routes.auditLog",
  "URL Shortener": "routes.urlShortener",
  Streams: "routes.streams",
  "Encode Cluster": "routes.encodeCluster",
  "Encode Cluster Config": "routes.encodeClusterConfig",
  "Input Cluster": "routes.inputCluster",
  "Input Cluster Config": "routes.inputClusterConfig",
  "Audience/API Cluster": "routes.apiCluster",
  "Audience/API Cluster Config": "routes.apiClusterConfig",
  "DB Cluster": "routes.dbCluster",
  "DB Cluster Config": "routes.dbClusterConfig",
  "Platform Status": "routes.platformStatus",
  Deployments: "routes.deployments",
  "Infra Config": "routes.infraClusterConfig",
  Audience: "routes.audience",
  "Audience Viewer": "routes.audienceViewer",
  "Quick Share": "routes.quickShare",
  Stream: "routes.stream",
  "Stream Diagnostics": "routes.streamDiagnostics",
  "Stream Activity": "routes.streamActivity",
  "Broadcast History Explorer": "routes.broadcastHistoryExplorer",
  "Platform Settings": "routes.platformSettings",
  Videos: "routes.videos",
  "File Browser": "routes.fileBrowser",
  "File Details": "routes.fileDetails",
  "Video Editor": "routes.videoEditor",
  "VTT Editor": "routes.vttEditor",
  Encoders: "routes.encoders",
  Encoder: "routes.encoder",
  VodAsset: "routes.vodAsset",
  VodAssetCreate: "routes.vodAsset",
  "Client Accounts": "routes.clientAccounts",
  "Client Account": "routes.clientAccount",
  "AI Assistant": "routes.aiAssistant",
  Documentation: "routes.documentation",
};

// Translated page title for a route; falls back to the raw route name for
// anything unmapped so new routes degrade visibly rather than blank.
export function routeTitle(route) {
  const key = ROUTE_TITLE_KEYS[route.name];
  if (key && i18nInstance) {
    return i18nInstance.global.t(key);
  }
  return route.name || "";
}
